import type { FunctionComponent } from "react";
import { Fragment } from "react";
import { graphql, usePaginationFragment } from "react-relay";

import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks";
import type { authorContentListAuthor$key } from "scmp-app/queries/__generated__/authorContentListAuthor.graphql";

import { AdSlotWrapper, Container, EmptyLabel, Item, StyledAdSlot, StyledLoading } from "./styles";

const authorPageSize = 30;

type Props = {
  reference: authorContentListAuthor$key;
};

export const AuthorContentList: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const {
    data,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment(
    graphql`
      fragment authorContentListAuthor on Author
      @argumentDefinitions(
        after: { type: "String" }
        applicationIds: { type: "[String]", defaultValue: [] }
        count: { type: "Int", defaultValue: 30 }
      )
      @refetchable(queryName: "authorContentListPaginationQuery") {
        contents(
          after: $after
          first: $count
          filter: { applicationIds: $applicationIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "authorContentList__author_contents") {
          edges {
            node {
              entityId
              ...secondaryContentItemContent
            }
          }
        }
        entityId
      }
    `,
    reference_,
  );
  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => {
      loadNext(authorPageSize);
    },
  });

  return (
    <Container>
      {data.contents?.edges.length ? (
        <>
          {data.contents?.edges.map(({ node }, index) => (
            <Fragment key={node.entityId}>
              <Item key={node.entityId} reference={node} />
              {index === 3 && (
                <AdSlotWrapper>
                  <StyledAdSlot
                    adUnit="d_lrec1"
                    sizes={[[300, 250]]}
                    targeting={{ paid: TargetingPaidType.NotArticle, scaid: data.entityId }}
                    withBackground
                    withLabel
                    zone="comment_author/home"
                  />
                </AdSlotWrapper>
              )}
            </Fragment>
          ))}
          {hasNextPage && (
            <StyledLoading ref={element => infiniteScrollTriggerReference(element)} />
          )}
        </>
      ) : (
        <EmptyLabel>No article</EmptyLabel>
      )}
    </Container>
  );
};
